import { useState, useMemo } from 'react';
import { Dialog, DialogTitle, DialogContent, TextField, List, ListItem, ListItemButton, ListItemText, Typography, Box, Chip, InputAdornment, CircularProgress, Divider } from '@mui/material';
import { Search, Building2, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePortfolioCompanies, useUsers, useFunds } from '@diggens/shared-hooks';
import { ICON_SIZES, ICON_STROKE } from '../constants/icons';
import type { PortfolioCompany } from '@diggens/types';

interface GlobalSearchDialogProps {
  open: boolean;
  onClose: () => void;
  tenantId: string;
}

export function GlobalSearchDialog({ open, onClose, tenantId }: GlobalSearchDialogProps) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch all data
  const { data: allCompanies, isLoading: companiesLoading } = usePortfolioCompanies();
  const { data: allUsers, isLoading: usersLoading } = useUsers(tenantId);
  const { data: funds } = useFunds(tenantId);

  // Filter for portco contacts only
  const portcoContacts = useMemo(() => {
    return allUsers?.filter((user) => user.role === 'portco' && user.portfolio_company_id) || [];
  }, [allUsers]);

  // Client-side filtering
  const filteredCompanies = useMemo(() => {
    if (!searchQuery || !allCompanies) return [];
    const query = searchQuery.toLowerCase();

    // Group companies by name to handle duplicates across funds
    const companiesByName = new Map<string, PortfolioCompany[]>();
    allCompanies.forEach((company) => {
      const name = company.name.toLowerCase();
      if (name.includes(query) || company.website?.toLowerCase().includes(query) || company.sector?.toLowerCase().includes(query)) {
        if (!companiesByName.has(name)) {
          companiesByName.set(name, []);
        }
        companiesByName.get(name)!.push(company);
      }
    });

    // Convert back to array, keeping all fund instances
    return Array.from(companiesByName.values()).flat().slice(0, 20);
  }, [searchQuery, allCompanies]);

  const filteredContacts = useMemo(() => {
    if (!searchQuery || !portcoContacts) return [];
    const query = searchQuery.toLowerCase();
    return portcoContacts.filter((contact) => contact.full_name?.toLowerCase().includes(query) || contact.email?.toLowerCase().includes(query)).slice(0, 10); // Limit results
  }, [searchQuery, portcoContacts]);

  const handleCompanyClick = (fundId: string, companyId: string, companyName: string) => {
    // Navigate to fund page with company ID and search query
    navigate(`/funds/${fundId}?companyId=${companyId}&search=${encodeURIComponent(companyName)}`);
    onClose();
    setSearchQuery('');
  };

  const handlePersonClick = (portfolioCompanyId: string) => {
    // Find the company to get its fund_id
    const company = allCompanies?.find((c) => c.id === portfolioCompanyId);
    if (company) {
      // Navigate to fund page with company ID, search, AND users tab selected
      navigate(`/funds/${company.fund_id}?companyId=${company.id}&search=${encodeURIComponent(company.name)}&tab=users`);
    }
    onClose();
    setSearchQuery('');
  };

  const getFundName = (fundId: string) => {
    return funds?.find((f) => f.id === fundId)?.name || 'Unknown Fund';
  };

  const isLoading = companiesLoading || usersLoading;
  const hasResults = filteredCompanies.length > 0 || filteredContacts.length > 0;
  const showResults = searchQuery.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Search Portfolio Companies & Contacts</Typography>
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          placeholder="Search by name, email, title, sector..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
              </InputAdornment>
            ),
            endAdornment: isLoading && (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ),
          }}
        />

        {showResults && (
          <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            {!hasResults && !isLoading && (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No results found
              </Typography>
            )}

            {/* Portfolio Companies */}
            {filteredCompanies.length > 0 && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Building2 size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />
                  <Typography variant="subtitle2" color="text.secondary">
                    PORTFOLIO COMPANIES ({filteredCompanies.length})
                  </Typography>
                </Box>
                <List disablePadding>
                  {filteredCompanies.map((company) => (
                    <ListItem key={company.id} disablePadding>
                      <ListItemButton onClick={() => handleCompanyClick(company.fund_id, company.id, company.name)} sx={{ borderRadius: 1 }}>
                        <ListItemText
                          primary={company.name}
                          secondary={
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                              <Chip label={getFundName(company.fund_id)} size="small" />
                              {company.sector && (
                                <Typography variant="caption" color="text.secondary">
                                  {company.sector}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {/* Divider between sections */}
            {filteredCompanies.length > 0 && filteredContacts.length > 0 && <Divider sx={{ my: 2 }} />}

            {/* Contacts */}
            {filteredContacts.length > 0 && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <User size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />
                  <Typography variant="subtitle2" color="text.secondary">
                    CONTACTS ({filteredContacts.length})
                  </Typography>
                </Box>
                <List disablePadding>
                  {filteredContacts.map((contact) => (
                    <ListItem key={contact.id} disablePadding>
                      <ListItemButton onClick={() => handlePersonClick(contact.portfolio_company_id!)} sx={{ borderRadius: 1 }} disabled={!contact.portfolio_company_id}>
                        <ListItemText
                          primary={contact.full_name || contact.email}
                          secondary={
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Typography variant="body2">{contact.email}</Typography>
                              {contact.portfolio_company_id && allCompanies && (
                                <Typography variant="caption" color="text.secondary">
                                  {allCompanies.find((c) => c.id === contact.portfolio_company_id)?.name || 'Unknown Company'}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Box>
        )}

        {!showResults && (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            Start typing to search...
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}
